import React, { useEffect, useState } from 'react';
import {
  Box, AppBar, Toolbar, Typography, Drawer, List, ListItem, Button, Menu, MenuItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton, Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import { Restaurant, CalendarMonth, Menu as MenuIcon, Logout, Spa, People, Key } from '@mui/icons-material';
import Login from './pages/Login';
import Gerichte from './pages/Gerichte';
import Wochenplan from './pages/Wochenplan';
import Saisonkalender from './pages/Saisonkalender';
import Benutzer from './pages/Benutzer';
import { PasswortAendern, PasswortSetzen } from './pages/PasswortDialoge';
import api from './api';
import { KontoContext, Rolle, ROLLEN } from './konto';

const DRAWER_WIDTH = 220;

// rollen: wer die Seite sieht (ohne Angabe: alle). Standard (Service) braucht keine Gerichte (K39).
const SEITEN: { name: string; icon: React.ReactNode; rollen?: Rolle[] }[] = [
  { name: 'Gerichte', icon: <Restaurant />, rollen: ['admin', 'planung'] },
  { name: 'Wochenplan', icon: <CalendarMonth /> },
  { name: 'Saisonkalender', icon: <Spa /> },
  { name: 'Benutzer', icon: <People />, rollen: ['admin'] },
];
const seitenFuer = (rolle: Rolle) => SEITEN.filter(s => !s.rollen || s.rollen.includes(rolle));

const gespeicherteRolle = (): Rolle => {
  const r = localStorage.getItem('rolle');
  return r === 'admin' || r === 'planung' || r === 'standard' ? r : 'standard';
};

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [userName, setUserName] = useState(localStorage.getItem('userName') || '');
  const [rolle, setRolle] = useState<Rolle>(gespeicherteRolle);
  const [eigeneId, setEigeneId] = useState<number | undefined>();
  const [aktiveSeite, setAktiveSeite] = useState(() => seitenFuer(gespeicherteRolle())[0].name);
  const [drawerOffen, setDrawerOffen] = useState(false);
  const [planBeschaeftigt, setPlanBeschaeftigt] = useState(false);
  const [kontoMenue, setKontoMenue] = useState<HTMLElement | null>(null);
  const [passwortOffen, setPasswortOffen] = useState(false);
  // Link aus "Passwort vergessen"/Einladung: ?reset=<token>
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get('reset'));
  // Suche, mit der die Gerichteübersicht geöffnet wird (z. B. aus dem Saisonkalender)
  const [gerichteSuche, setGerichteSuche] = useState<{ text: string; nr: number } | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const kompakt = aktiveSeite === 'Wochenplan' && !isMobile;
  const breite = kompakt ? 64 : DRAWER_WIDTH;
  const seiten = seitenFuer(rolle);

  const handleLogin = (t: string, name: string, r: Rolle) => {
    localStorage.setItem('token', t);
    localStorage.setItem('userName', name);
    localStorage.setItem('rolle', r);
    setToken(t);
    setUserName(name);
    setRolle(r);
    setAktiveSeite(seitenFuer(r)[0].name);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('rolle');
    setToken('');
    setUserName('');
  };

  useEffect(() => {
    const expired = () => { setToken(''); setUserName(''); };
    window.addEventListener('session-expired', expired);
    return () => window.removeEventListener('session-expired', expired);
  }, []);

  // Rolle und Name bei jedem Start vom Server holen (Rolle kann sich geändert haben)
  useEffect(() => {
    if (!token) return;
    api.get('/auth/ich').then(res => {
      setRolle(res.data.rolle);
      setUserName(res.data.name);
      setEigeneId(res.data.id);
      localStorage.setItem('rolle', res.data.rolle);
      localStorage.setItem('userName', res.data.name);
    }).catch(() => { /* 401 meldet der api-Interceptor als abgelaufene Sitzung */ });
  }, [token]);

  // Seite, die zur (evtl. geänderten) Rolle nicht passt, durch die erste erlaubte ersetzen
  useEffect(() => {
    const erlaubt = seitenFuer(rolle);
    if (!erlaubt.some(s => s.name === aktiveSeite)) setAktiveSeite(erlaubt[0].name);
  }, [aktiveSeite, rolle]);

  if (resetToken) {
    return <PasswortSetzen token={resetToken} onFertig={() => { window.history.replaceState(null, '', window.location.pathname); setResetToken(null); }} />;
  }
  if (!token) return <Login onLogin={handleLogin} />;

  const drawer = (
    <Box>
      <Toolbar>
        {!kompakt && <Typography variant="h6" fontWeight="bold" color="primary">Menüplan</Typography>}
      </Toolbar>
      <List>
        {seiten.map(s => (
          <ListItem key={s.name} disablePadding>
            <Tooltip title={kompakt ? s.name : ''} placement="right">
            <ListItemButton
              aria-label={s.name}
              disabled={planBeschaeftigt && s.name !== aktiveSeite}
              selected={aktiveSeite === s.name}
              onClick={() => { setAktiveSeite(s.name); setDrawerOffen(false); if (s.name === 'Gerichte') setGerichteSuche(null); }}
            >
              <ListItemIcon sx={{ minWidth: kompakt ? 32 : 56 }}>{s.icon}</ListItemIcon>
              {!kompakt && <ListItemText primary={s.name} />}
            </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <KontoContext.Provider value={rolle}>
    <Box display="flex">
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {isMobile && (
            <IconButton aria-label="Navigation öffnen" color="inherit" edge="start" onClick={() => setDrawerOffen(true)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" flexGrow={1} noWrap>
            {isMobile ? 'Menüdatenbank' : 'Hotel Hohenlohe — Menüdatenbank'}
          </Typography>
          <Button color="inherit" onClick={e => setKontoMenue(e.currentTarget)} sx={{ textTransform: 'none', mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}>
            {userName}
          </Button>
          <IconButton color="inherit" aria-label="Konto" onClick={e => setKontoMenue(e.currentTarget)} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
            <Key />
          </IconButton>
          <IconButton disabled={planBeschaeftigt} aria-label="Abmelden" color="inherit" onClick={handleLogout}><Logout /></IconButton>
        </Toolbar>
      </AppBar>
      <Menu anchorEl={kontoMenue} open={!!kontoMenue} onClose={() => setKontoMenue(null)}>
        <MenuItem disabled dense>{userName} · {ROLLEN.find(r => r.wert === rolle)?.label}</MenuItem>
        <MenuItem onClick={() => { setKontoMenue(null); setPasswortOffen(true); }}>
          <ListItemIcon><Key fontSize="small" /></ListItemIcon>
          <ListItemText>Passwort ändern</ListItemText>
        </MenuItem>
      </Menu>
      {passwortOffen && <PasswortAendern onClose={() => setPasswortOffen(false)} />}

      {isMobile ? (
        <Drawer open={drawerOffen} onClose={() => setDrawerOffen(false)} sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
          {drawer}
        </Drawer>
      ) : (
        <Drawer variant="permanent" sx={{ width: breite, flexShrink: 0, '& .MuiDrawer-paper': { width: breite } }}>
          {drawer}
        </Drawer>
      )}

      <Box component="main" flexGrow={1} p={kompakt ? 2 : 3} mt={8} sx={{ minWidth: 0 }}>
        {aktiveSeite === 'Gerichte' && rolle !== 'standard' && (
          <Gerichte key={gerichteSuche?.nr ?? 0} startSuche={gerichteSuche?.text} />
        )}
        {aktiveSeite === 'Wochenplan' && <Wochenplan onBusyChange={setPlanBeschaeftigt} />}
        {aktiveSeite === 'Saisonkalender' && (
          <Saisonkalender onGerichteZeigen={rolle === 'standard' ? undefined : text => { setGerichteSuche({ text, nr: Date.now() }); setAktiveSeite('Gerichte'); }} />
        )}
        {aktiveSeite === 'Benutzer' && rolle === 'admin' && <Benutzer eigeneId={eigeneId} />}
      </Box>
    </Box>
    </KontoContext.Provider>
  );
}
